import { z } from 'zod';
import { ApiSuccessResponseSchema } from './api';

/**
 * User-Related Schemas
 * 
 * Epic 2 - Story 2.3: Runtime Type Safety
 * 
 * These schemas validate user search and user info responses.
 */

/**
 * User Info Schema
 * Represents the aggregated user object returned by the backend /user endpoint.
 * This combines Okta, Active Directory, job, location, and MFA factor data.
 */
export const UserInfoSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
  okta: z.object({
    id: z.string().optional(),
    status: z.string().optional(),
    secondEmail: z.string().nullable().optional(),
    created: z.string().optional(),
    lastLogin: z.string().nullable().optional(),
  }).passthrough().optional(),
  job: z.object({
    employeeType: z.string().optional(),
    title: z.string().optional(),
    department: z.string().optional(),
    division: z.string().nullable().optional(),
    company: z.string().optional(),
    costCenter: z.string().optional(),
    employeeNumber: z.string().optional(),
    nikeJobCode: z.string().nullable().optional(),
    manager: z.object({
      email: z.string().optional(),
      name: z.string().optional(),
      username: z.string().optional(),
    }).passthrough().optional(),
  }).passthrough().optional(),
  location: z.object({
    city: z.string().optional(),
    state: z.string().optional(),
    countryCode: z.string().optional(),
    officeLocation: z.string().nullable().optional(),
    streetAddress: z.string().nullable().optional(),
    zipCode: z.string().nullable().optional(),
    nike_cubeLocation: z.string().nullable().optional(),
  }).passthrough().optional(),
  activeDirectory: z.object({
    locked: z.boolean().optional(),
    accountExpired: z.boolean().optional(),
    accountExpires: z.string().nullable().optional(),
    passwordExpired: z.boolean().optional(),
    passwordExpires: z.string().nullable().optional(),
    passwordLastSet: z.string().nullable().optional(),
    username: z.string().optional(),
    disabled: z.boolean().optional(),
    passwordNeverExpires: z.boolean().optional(),
    samAccountName: z.string().optional(),
    userPrincipalName: z.string().optional(),
    created: z.string().optional(),
    lastModified: z.string().optional(),
  }).passthrough().optional(),
  factors: z.array(z.object({
    id: z.string().optional(),
    factorType: z.string().optional(),
    name: z.string().nullable().optional(),
    status: z.string().optional(),
    provider: z.string().optional(),
    phoneNumber: z.string().nullable().optional(),
    platform: z.string().nullable().optional(),
    lastUsed: z.string().nullable().optional(),
    created: z.string().optional(),
    _links: z.record(z.string(), z.unknown()).optional(),
  }).passthrough()).optional(),
}).passthrough();

export type UserInfo = z.infer<typeof UserInfoSchema>;

/**
 * Find User By Email Response
 */
export const FindUserByEmailResponseSchema = ApiSuccessResponseSchema(UserInfoSchema);

export type FindUserByEmailResponse = z.infer<typeof FindUserByEmailResponseSchema>;

/**
 * Find GID By Username Response
 */
export const FindGIDByUsernameResponseSchema = ApiSuccessResponseSchema(UserInfoSchema);

export type FindGIDByUsernameResponse = z.infer<typeof FindGIDByUsernameResponseSchema>;

/**
 * Unlock Account Response
 */
export const UnlockAccountResponseSchema = ApiSuccessResponseSchema(
  z.object({
    message: z.string().optional(),
  })
);

export type UnlockAccountResponse = z.infer<typeof UnlockAccountResponseSchema>;

/**
 * Add to MFA Pilot Response
 */
export const AddToMfaPilotResponseSchema = ApiSuccessResponseSchema(
  z.object({
    message: z.string().optional(),
    groupId: z.string().optional(),
  })
);

export type AddToMfaPilotResponse = z.infer<typeof AddToMfaPilotResponseSchema>;

